import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { EffectiveOwnership, LeagueStandingsPage, LeagueSummary, SquadPlayer } from '@fantasy/types';
import { api, ApiRequestError } from '../api.ts';
import { LeagueInsights } from './LeagueInsights.tsx';
import { useSquad } from '../state/useSquad.ts';
import { Term } from './Term.tsx';

/**
 * Rank movement since the previous gameweek, read off `previousRank` — the
 * standings entry only ever carries the two raw ranks, never a precomputed
 * delta. `null` before a league has any history yet (gameweek 1, or a
 * manager who has just joined).
 */
function movement(rank: number, previousRank: number | null): { delta: number; label: string } {
  if (previousRank === null) return { delta: 0, label: 'Not ranked last gameweek' };
  const delta = previousRank - rank;
  if (delta === 0) return { delta, label: 'Unchanged since last gameweek' };
  if (delta > 0) return { delta, label: `Up ${delta} since last gameweek` };
  return { delta, label: `Down ${Math.abs(delta)} since last gameweek` };
}

/** The direction and size read as text (▲/▼ plus the digit), not colour
 * alone — legible in greyscale or to a colourblind manager, and the
 * `sr-only` span spells the same thing out for a screen reader that
 * wouldn't otherwise get anything from an arrow glyph. */
function MovementCell({ rank, previousRank }: { rank: number; previousRank: number | null }) {
  const { delta, label } = movement(rank, previousRank);
  const symbol = delta > 0 ? '▲' : delta < 0 ? '▼' : '–';

  return (
    <td className="league-movement" data-direction={delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'}>
      <span aria-hidden="true">
        {symbol}
        {delta !== 0 && Math.abs(delta)}
      </span>
      <span className="sr-only">{label}</span>
    </td>
  );
}

interface LeagueStandingsTableProps {
  standings: LeagueStandingsPage;
  ownEntryId: number | null;
}

function LeagueStandingsTable({ standings, ownEntryId }: LeagueStandingsTableProps) {
  return (
    <div className="scroll-x">
      <table>
        <caption className="hint table-caption">
          Gameweek points and total points, ranked highest total first.
        </caption>
        <thead>
          <tr>
            <th scope="col">Rank</th>
            <th scope="col">Movement</th>
            <th scope="col">Team</th>
            <th scope="col">Manager</th>
            <th scope="col" className="num">
              GW pts
            </th>
            <th scope="col" className="num">
              Total pts
            </th>
          </tr>
        </thead>
        <tbody>
          {standings.entries.map((entry) => {
            const isOwn = entry.entryId === ownEntryId;
            return (
              <tr key={entry.entryId} className={isOwn ? 'league-row--own' : undefined}>
                <th scope="row">{entry.rank}</th>
                <MovementCell rank={entry.rank} previousRank={entry.previousRank} />
                <td>{entry.teamName}</td>
                <td>
                  {entry.managerName}
                  {isOwn && <span className="pill pill--own">You</span>}
                </td>
                <td className="num">{entry.gameweekPoints}</td>
                <td className="num">{entry.totalPoints}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The mini-league standings screen (T-054) — depends on T-053's `/api/leagues`
 * endpoints. A manager can belong to several classic leagues; this picks the
 * first by default and lets them switch, mirroring `FdrGrid`'s horizon
 * buttons rather than a `<select>` (nothing else in this app uses one).
 */
export function LeagueStandings() {
  const [leagues, setLeagues] = useState<LeagueSummary[] | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [ownEntryId, setOwnEntryId] = useState<number | null>(null);
  const [ownPlayers, setOwnPlayers] = useState<SquadPlayer[]>([]);
  const [leaguesError, setLeaguesError] = useState<string | null>(null);
  const [leaguesLoading, setLeaguesLoading] = useState(true);

  // Lives in SquadProvider, not local state here — same pattern T-056
  // established for objective/planHorizon, so the league picked here is the
  // same one `analyseSquad`/`runTheDesk` send as `leagueId` (T-078).
  const { leagueId, setLeagueId } = useSquad();
  const [page, setPage] = useState(1);
  const [standings, setStandings] = useState<LeagueStandingsPage | null>(null);
  const [standingsError, setStandingsError] = useState<string | null>(null);
  const [standingsLoading, setStandingsLoading] = useState(false);

  const [ownership, setOwnership] = useState<EffectiveOwnership | null>(null);
  const [ownershipError, setOwnershipError] = useState<string | null>(null);

  // Own leagues plus whichever saved squad carries an entry id — fetched
  // together since both are needed before the standings table can render
  // anything meaningful (which league to default to, and whose row is
  // "yours"). Also carries that squad's own players, so the effective-
  // ownership table below can show them by name rather than a bare fplId.
  useEffect(() => {
    let cancelled = false;
    setLeaguesLoading(true);
    setLeaguesError(null);
    Promise.all([api.getLeagues(), api.listSquads()])
      .then(([leaguesResult, squadsResult]) => {
        if (cancelled) return;
        setLeagues(leaguesResult.leagues);
        setReason(leaguesResult.reason);
        const withEntryId = squadsResult.squads.find((squad) => squad.entryId !== undefined);
        setOwnEntryId(withEntryId?.entryId ?? null);
        setOwnPlayers(withEntryId?.players ?? []);
        // Only default when nothing has been picked yet — a league already
        // chosen (this session, or restored from context) must not be
        // silently swapped out from under a manager who's browsing it.
        if (leagueId === null) setLeagueId(leaguesResult.leagues[0]?.id ?? null);
      })
      .catch((err) => {
        if (!cancelled) {
          setLeaguesError(err instanceof ApiRequestError ? err.message : 'Could not load your leagues.');
        }
      })
      .finally(() => {
        if (!cancelled) setLeaguesLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately
    // mount-only, same as SquadView's own fetchLivePointsSignal effect;
    // `leagueId`/`setLeagueId` are only read here to seed a first-time
    // default, not to re-run this fetch when they change.
  }, []);

  useEffect(() => {
    if (leagueId === null) return;
    let cancelled = false;
    setStandingsLoading(true);
    setStandingsError(null);
    api
      .getLeagueStandings(leagueId, page)
      .then(({ standings: fetched }) => {
        if (!cancelled) setStandings(fetched);
      })
      .catch((err) => {
        if (!cancelled) {
          setStandingsError(err instanceof ApiRequestError ? err.message : 'Could not load standings.');
        }
      })
      .finally(() => {
        if (!cancelled) setStandingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId, page]);

  // Effective ownership (T-078) — independent of the standings page above,
  // and never blocks it: a failed or absent lookup leaves the standings
  // table, and everything else on this screen, working exactly as before.
  useEffect(() => {
    setOwnership(null);
    setOwnershipError(null);
    if (leagueId === null) return;
    let cancelled = false;
    api
      .getEffectiveOwnership(leagueId)
      .then(({ ownership: fetched }) => {
        if (!cancelled) setOwnership(fetched);
      })
      .catch((err) => {
        if (!cancelled) {
          setOwnershipError(
            err instanceof ApiRequestError ? err.message : 'Could not load effective ownership.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  function onSelectLeague(id: number) {
    setLeagueId(id);
    setPage(1);
    setStandings(null);
  }

  return (
    <section className="panel panel--accent" aria-labelledby="leagues-heading">
      <div className="panel-head">
        <div>
          <h3 id="leagues-heading">Leagues</h3>
          <p className="hint">Where you stand in your mini-leagues, gameweek by gameweek.</p>
        </div>
      </div>

      {leaguesLoading && (
        <p className="hint" aria-live="polite">
          Loading your leagues…
        </p>
      )}

      {!leaguesLoading && leaguesError && (
        <ul className="notices notices--flush">
          <li className="error">{leaguesError}</li>
        </ul>
      )}

      {!leaguesLoading && !leaguesError && leagues && leagues.length === 0 && (
        <div>
          <p className="hint">
            {reason ?? 'No leagues to show yet.'}
          </p>
          <Link to="/squad" className="ghost small">
            Import your squad
          </Link>
        </div>
      )}

      {!leaguesLoading && !leaguesError && leagues && leagues.length > 0 && (
        <>
          {leagues.length > 1 && (
            <div className="field field--grow mt-md">
              <label id="league-picker-label">League</label>
              <div className="row row--flush" role="group" aria-labelledby="league-picker-label">
                {leagues.map((league) => (
                  <button
                    key={league.id}
                    type="button"
                    className="ghost small"
                    aria-pressed={leagueId === league.id}
                    onClick={() => onSelectLeague(league.id)}
                  >
                    {league.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {standingsLoading && (
            <p className="hint mt-md" aria-live="polite">
              Loading standings…
            </p>
          )}

          {!standingsLoading && standingsError && (
            <ul className="notices notices--flush mt-md">
              <li className="error">{standingsError}</li>
            </ul>
          )}

          {!standingsLoading && !standingsError && standings && (
            <>
              <LeagueStandingsTable standings={standings} ownEntryId={ownEntryId} />

              <div className="row row--flush mt-md">
                <button
                  type="button"
                  className="ghost small"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page <= 1}
                >
                  Previous
                </button>
                {standings.hasNext && (
                  <button type="button" className="ghost small" onClick={() => setPage((p) => p + 1)}>
                    Next
                  </button>
                )}
              </div>
            </>
          )}

          {leagueId !== null && (
            <LeagueInsights leagueId={leagueId} leagueName={standings?.name} standings={standings} />
          )}

          {ownershipError && (
            <ul className="notices notices--flush mt-md">
              <li className="error">{ownershipError}</li>
            </ul>
          )}

          {ownership && (
            <EffectiveOwnershipTable ownership={ownership} players={ownPlayers} leagueName={standings?.name} />
          )}
        </>
      )}
    </section>
  );
}

interface EffectiveOwnershipTableProps {
  ownership: EffectiveOwnership;
  /** The manager's own squad players — the only ones we can show by name;
   * see this component's own comment for why the table is scoped to them. */
  players: SquadPlayer[];
  leagueName: string | undefined;
}

/** How many of this league's own top rivals actually hold and captain each
 * of the manager's players (T-078) — not the global `selectedByPercent`
 * figure. Scoped to the manager's own squad rather than every player in the
 * game: `EffectiveOwnership` is keyed by FPL element id with no player name
 * attached, and the squad already on screen is the only source of names this
 * page has without a second, unrelated lookup. Denominator stated plainly —
 * "top N of this league", not "the league" — so it can't be misread as a
 * global figure. */
function EffectiveOwnershipTable({ ownership, players, leagueName }: EffectiveOwnershipTableProps) {
  const named = players.filter((player): player is SquadPlayer & { fplId: number } => player.fplId !== undefined);
  if (named.length === 0) return null;

  return (
    <div className="mt-lg">
      <h4>
        <Term term="effectiveOwnership">Effective ownership</Term>
        {leagueName ? ` in ${leagueName}` : ''}
      </h4>
      <p className="hint">
        How many of the top {ownership.rivalCap} of this league ({ownership.rivalsRead} actually read) own
        and captain each of your players — not the global ownership figure. A captain counts twice, so
        this goes above 100% for a player most of the league both owns and captains.
      </p>
      <div className="scroll-x">
        <table>
          <caption className="hint table-caption">
            Your squad, against the top {ownership.rivalCap} of this league.
          </caption>
          <thead>
            <tr>
              <th scope="col">Player</th>
              <th scope="col" className="num">
                Owned by
              </th>
              <th scope="col" className="num">
                Captained by
              </th>
              <th scope="col" className="num">
                Effective ownership
              </th>
            </tr>
          </thead>
          <tbody>
            {named.map((player) => {
              const entry = ownership.byFplId[String(player.fplId)];
              return (
                <tr key={player.fplId}>
                  <td>{player.name}</td>
                  <td className="num">{entry?.ownedBy ?? 0}</td>
                  <td className="num">{entry?.captainedBy ?? 0}</td>
                  <td className="num">{Math.round(entry?.effectiveOwnershipPercent ?? 0)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
